/**
 * TaskTimeline — live step-by-step execution progress.
 * Steps tick from pending → doing → done as the agent executes multi-step tasks.
 */
import { Check, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export interface TaskStep {
  label: string;
  status: "pending" | "doing" | "done";
}

interface TaskTimelineProps {
  steps: TaskStep[];
  className?: string;
}

export const TaskTimeline = ({ steps, className }: TaskTimelineProps) => {
  return (
    <div className={cn("mt-2 rounded-lg p-3 bg-gray7/50 border border-gray6", className)}>
      <div className="text-[10px] font-bold uppercase tracking-widest mb-2 text-gray3">
        Doing this now
      </div>
      <div className="space-y-1.5">
        {steps.map((s, i) => (
          <div key={i} className="flex items-center gap-2 text-[12px]">
            <div
              className={cn(
                "w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold shrink-0",
                s.status === "done" && "bg-green2 text-white",
                s.status === "doing" && "bg-white border-2 border-greentab",
                s.status === "pending" && "bg-gray6 text-gray3"
              )}
            >
              {s.status === "done" && <Check className="h-2.5 w-2.5" />}
              {s.status === "doing" && <Loader2 className="h-2.5 w-2.5 text-greentab animate-spin" />}
            </div>
            <span
              className={cn(
                s.status === "pending" ? "text-gray4" : "text-black1",
                s.status === "doing" && "font-semibold"
              )}
            >
              {s.label}
            </span>
            {s.status === "doing" && (
              <Badge className="ml-auto text-[10px] bg-green4 text-green2 border-green4">
                running…
              </Badge>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
